import { useEffect, useMemo, useState } from 'react';

import { StatusCode } from '../../../api/enumerations';
import { getAllWorkOrders } from '../../../api/workOrders';
import {
  GetWorkOrdersParams,
  WorkOrderData,
} from '../../../api/workOrders/types';
import {
  dedupeWorkOrdersById,
  flattenWorkOrders,
} from '../../../api/workOrders/utils';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useSnackbar from '../../../hooks/useSnackbar';
import { applyClientSideFilters } from '../../Home/hooks/extendedFilters';
import { computeBigWindow, filterByCreatedAt } from '../utils/derive';
import { dedupe, isStale, readCache, writeCache } from '../utils/swrCache';
import { DashboardFilters } from './useDashboardFilters';

const buildCacheKey = (
  start: string,
  end: string,
  filters: DashboardFilters
): string =>
  [
    `r=${start}_${end}`,
    `u=${filters.uf ?? ''}`,
    `c=${filters.companyId ?? ''}`,
    `ck=${[...filters.clientKinds].sort().join(',')}`,
    `rf=${[...filters.reportFunctions].sort().join(',')}`,
  ].join('|');

const fetchBigWindow = async (
  start: string,
  end: string,
  filters: DashboardFilters
): Promise<WorkOrderData[]> => {
  const params: GetWorkOrdersParams = {
    startDate: start,
    endDate: end,
    uf: filters.uf,
    licenseCompanyId: filters.companyId,
  };
  const response = await getAllWorkOrders(params);
  if (response.detail.description) throw new Error(response.detail.description);
  if (response.detail.status_code !== StatusCode.OK) {
    throw new Error('Algo deu errado, tente novamente.');
  }
  const flat = dedupeWorkOrdersById(flattenWorkOrders(response.data));
  // client_kind and report_function are applied client-side until backend
  // accepts these query params; see applyClientSideFilters TODO.
  const filtered = applyClientSideFilters(flat, {
    evaluationType: undefined,
    reportFunction:
      filters.reportFunctions.length > 0 ? filters.reportFunctions : undefined,
    status: undefined,
    sla: undefined,
  });
  if (filters.clientKinds.length === 0) return filtered;
  const set = new Set(filters.clientKinds);
  return filtered.filter((r) => set.has(r.client_kind));
};

export interface DashboardDataset {
  current: WorkOrderData[];
  previous: WorkOrderData[];
  last12m: WorkOrderData[];
}

export interface UseDashboardDataReturn {
  loading: boolean;
  refreshing: boolean;
  stale: boolean;
  data: DashboardDataset;
}

const EMPTY: DashboardDataset = { current: [], previous: [], last12m: [] };

export function useDashboardData(
  filters: DashboardFilters
): UseDashboardDataReturn {
  const bigWindow = useMemo(
    () => computeBigWindow(filters.period),
    [filters.period]
  );
  const cacheKey = useMemo(
    () => buildCacheKey(bigWindow.range.start, bigWindow.range.end, filters),
    [bigWindow, filters]
  );

  const [bigRows, setBigRows] = useState<WorkOrderData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stale, setStale] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  useEffect(() => {
    let cancelled = false;

    const cached = readCache<WorkOrderData[]>(cacheKey);
    if (cached) {
      setBigRows(cached.data);
      setLoading(false);
      const staleNow = isStale(cached.ts);
      setStale(staleNow);
      if (!staleNow) {
        setRefreshing(false);
        return () => {
          cancelled = true;
        };
      }
      setRefreshing(true);
    } else {
      setBigRows(null);
      setLoading(true);
      setRefreshing(false);
      setStale(false);
    }

    dedupe(cacheKey, () =>
      fetchBigWindow(bigWindow.range.start, bigWindow.range.end, filters)
    )
      .then((rows) => {
        if (cancelled) return;
        writeCache(cacheKey, rows);
        setBigRows(rows);
        setStale(false);
      })
      .catch((error) => {
        if (cancelled) return;
        handleSnackbar(getErrorMessage(error), true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const data = useMemo<DashboardDataset>(() => {
    if (!bigRows) return EMPTY;
    return {
      current: filterByCreatedAt(bigRows, bigWindow.current),
      previous: filterByCreatedAt(bigRows, bigWindow.previous),
      last12m: filterByCreatedAt(bigRows, bigWindow.last12m),
    };
  }, [bigRows, bigWindow]);

  return { loading, refreshing, stale, data };
}
